import { api, handleApiError, ApiError, TOKEN_KEY, setUnauthorizedHandler } from '../apiService';

// Additional constants
const USER_KEY = "user";
const REMEMBER_ME_KEY = "rememberMe";

// Main service object
const englishService = {
  auth: {
    login: async (credentials) => {
      try {
        const response = await api.post("/api/auth/login", credentials);
        if (response.data.token) {
          localStorage.setItem(TOKEN_KEY, response.data.token);
          localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
        }
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },

    logout: () => {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(REMEMBER_ME_KEY);
    },

    getCurrentUser: () => {
      try {
        const userStr = localStorage.getItem(USER_KEY);
        return userStr ? JSON.parse(userStr) : null;
      } catch (error) {
        console.error("Error parsing user data:", error);
        return null;
      }
    },

    isAuthenticated: () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return false;

      try {
        const user = englishService.auth.getCurrentUser();
        return !!user;
      } catch {
        return false;
      }
    },

    getAuthHeader: () => {
      const token = localStorage.getItem(TOKEN_KEY);
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
  },

  // Enhanced CRUD service generator
  createCrudService: (endpoint) => ({
    getAll: async (params = {}) => {
      try {
        const response = await api.get(`/api/${endpoint}`, { params });
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },

    getById: async (id) => {
      try {
        const response = await api.get(`/api/${endpoint}/${id}`);
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },

    create: async (data) => {
      try {
        const response = await api.post(`/api/${endpoint}`, data);
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },

    update: async (id, data) => {
      try {
        const response = await api.put(`/api/${endpoint}/${id}`, data);
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },

    delete: async (id) => {
      try {
        const response = await api.delete(`/api/${endpoint}/${id}`);
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },
  }),

  // Stats service to fetch counts directly from /api/stats
  getStats: async () => {
    try {
      const response = await api.get('/api/stats');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Simple visitor stats service
  visits: {
    getByPeriod: async (period = 'week') => {
      try {
        const response = await api.get('/api/visits/by-period', { params: { period } });
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    }
  }
};

// Initialize CRUD services for each endpoint
[
  "news",
  "blogs",
  "articles",
  "speeches",
  "events",
  "interviews",
  "projects",
  "messages",
  "sccnMessages",
  "marathiBlogs",
  "ovenfresh",
  "subscribers"
].forEach((endpoint) => {
  englishService[endpoint] = englishService.createCrudService(endpoint);
});

// Add comments service with additional methods
englishService.comments = {
  ...englishService.createCrudService('comments'),
  
  // Additional custom endpoints for comments
  updateStatus: async (id, status) => {
    try {
      const response = await api.put(`/api/comments/${id}/status`, { status });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getByContentType: async (contentType) => {
    try {
      const response = await api.get(`/api/comments/type/${contentType}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getByContentId: async (contentType, contentId) => {
    try {
      const response = await api.get(`/api/comments/${contentType}/${contentId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// Set the unauthorized handler
setUnauthorizedHandler(() => {
  englishService.auth.logout();
  window.location.href = "/login";
});

// Export the ApiError class along with the englishService
export { ApiError };
export default englishService;